<script setup>
import { computed, ref, nextTick, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import { useCopilot } from 'shared/composables/useCopilot';
import { useCopilotAttachments } from 'shared/composables/useCopilotAttachments';
import {
  useCopilotStore,
  getThreadTitle,
  deriveThreadTitle,
} from 'dashboard/stores/copilot';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import {
  buildAttachedContext,
  buildAttachedContextPreview,
  stripAttachedContext,
} from 'shared/helpers/copilotMentionContext';
import { mentionItemKey } from 'shared/helpers/copilotMentionSearch';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import {
  getToolLabel,
  getToolStatusLabel,
} from 'shared/utils/copilotToolLabels';
import Button from 'dashboard/components-next/button/Button.vue';
import CopilotMessage from './CopilotMessage.vue';
import CopilotToolCall from './CopilotToolCall.vue';
import CopilotSubagent from './CopilotSubagent.vue';
import CopilotInput from './CopilotInput.vue';
import CopilotConfirmation from './CopilotConfirmation.vue';
import { getTurnReasoningMeta } from 'shared/helpers/copilotReasoning';
import {
  useCopilotTurnScroll,
  findLastHumanIndex,
  findLastAssistantIndex,
} from 'shared/composables/copilot/useCopilotTurnScroll';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
  apiUrl: { type: String, default: '' },
  assistantType: { type: String, default: 'copilot' },
});
const emit = defineEmits(['openPanel', 'update:assistantType']);
const PENDING_HUMAN_ID = '__pending__';
const PENDING_AI_ID = '__pending_ai__';

function messageContentKey(content) {
  if (typeof content === 'string') return stripAttachedContext(content);
  if (Array.isArray(content)) {
    const text = content
      .filter(block => block.type === 'text')
      .map(block => block.text)
      .join('\n');
    return stripAttachedContext(text);
  }
  return '';
}

const { t, tm, locale } = useI18n();
const messageListRef = ref(null);
const transcriptSizerRef = ref(null);
const messageRefs = ref({});
const lastMessageRef = ref(null);
const holdActiveAiKey = ref(false);
let holdActiveAiKeyTimer = null;

const {
  isActiveTurn,
  sizerExcessPx,
  sizerExcessStyle,
  beginTurn,
  refreshTurn,
  finishTurn,
  clearTurn,
  handleTurnScroll,
  pinScrollToBottom,
} = useCopilotTurnScroll(messageListRef, () => transcriptSizerRef.value);

function setMessageRef(rowKey, el) {
  if (el) {
    messageRefs.value[rowKey] = el;
  } else {
    delete messageRefs.value[rowKey];
  }
  const msgs = displayedMessages.value;
  if (!msgs.length) return;
  const lastIndex = msgs.length - 1;
  const lastKey = transcriptRowKey(msgs[lastIndex], lastIndex);
  if (rowKey === lastKey) {
    lastMessageRef.value = el;
  }
  if (isActiveTurn.value && el && rowKey === 'active-human') {
    refreshTurnLayout(`message-ref-${rowKey}`);
  }
}

function messageType(msg) {
  return msg?.type || msg?._getType?.() || '';
}

function isTightToPrevious(index) {
  if (messageType(displayedMessages.value[index]) !== 'ai') return false;
  for (let i = index - 1; i >= 0; i -= 1) {
    const type = messageType(displayedMessages.value[i]);
    if (type === 'tool' || type === 'system') continue;
    return type === 'ai';
  }
  return false;
}

function transcriptRowKey(msg, index) {
  if (msg?.id === PENDING_HUMAN_ID) return 'active-human';

  const type = msg?.type || msg?._getType?.();
  const msgs = displayedMessages.value;
  const lastHumanIdx = findLastHumanIndex(msgs);
  if (
    type === 'human' &&
    index === lastHumanIdx &&
    (isLoading.value || isActiveTurn.value)
  ) {
    return 'active-human';
  }

  if (msg?.id === PENDING_AI_ID) return 'active-ai';

  const isCurrentTurnAi =
    type === 'ai' && index === msgs.length - 1 && index === lastHumanIdx + 1;

  if (
    isCurrentTurnAi &&
    (isLoading.value || isActiveTurn.value || holdActiveAiKey.value)
  ) {
    return 'active-ai';
  }

  return msg?.id || `msg-${index}`;
}

function getLastHumanRow() {
  const humanIndex = findLastHumanIndex(displayedMessages.value);
  if (humanIndex < 0) return null;
  const msg = displayedMessages.value[humanIndex];
  return messageRefs.value[transcriptRowKey(msg, humanIndex)] ?? null;
}

function getLastAssistantRow() {
  const assistantIndex = findLastAssistantIndex(displayedMessages.value);
  if (assistantIndex < 0) return null;
  const msg = displayedMessages.value[assistantIndex];
  return messageRefs.value[transcriptRowKey(msg, assistantIndex)] ?? null;
}

function getActiveAnchorKey() {
  const humanIndex = findLastHumanIndex(displayedMessages.value);
  if (humanIndex < 0) return null;
  const msg = displayedMessages.value[humanIndex];
  return transcriptRowKey(msg, humanIndex);
}

function beginTurnAfterSend(reason = 'beginTurnAfterSend') {
  beginTurn(getLastHumanRow, getLastAssistantRow, getActiveAnchorKey, reason);
}

function refreshTurnLayout(reason = 'refreshTurnLayout') {
  if (!isActiveTurn.value) return;
  refreshTurn(reason);
}
const store = useCopilotStore();
const currentUser = useMapGetter('getCurrentUser');

const firstName = computed(() => {
  const name = currentUser.value?.name || '';
  return name.split(' ')[0];
});

const greetingTitle = ref(t('COPILOT.EMPTY_TITLE'));

function pickGreetingTitle() {
  const name = firstName.value;
  if (!name) {
    greetingTitle.value = t('COPILOT.EMPTY_TITLE');
    return;
  }
  const variants = tm('COPILOT.GREETING_VARIANTS');
  const keys = Object.keys(variants);
  if (!keys.length) {
    greetingTitle.value = t('COPILOT.EMPTY_TITLE');
    return;
  }
  const key = keys[Math.floor(Math.random() * keys.length)];
  greetingTitle.value = t(`COPILOT.GREETING_VARIANTS.${key}`, { name });
}

watch([firstName, locale], pickGreetingTitle);
// assistantType is now a prop — mutations emit upward so CopilotPage can use it as :key
const assistantType = computed({
  get: () => props.assistantType,
  set: val => emit('update:assistantType', val),
});

const betaEnabled = computed(
  () => !!window.globalConfig?.COPILOT_BETA_ENABLED
);

const assistants = computed(() => {
  const list = [
    {
      id: 'copilot',
      name: t('COPILOT.ASSISTANTS.COPILOT.NAME'),
      description: t('COPILOT.ASSISTANTS.COPILOT.DESCRIPTION'),
      icon: 'i-lucide-sparkles',
    },
    {
      id: 'seo',
      name: t('COPILOT.ASSISTANTS.SEO.NAME'),
      description: t('COPILOT.ASSISTANTS.SEO.DESCRIPTION'),
      icon: 'i-lucide-search',
    },
    {
      id: 'sales',
      name: t('COPILOT.ASSISTANTS.SALES.NAME'),
      description: t('COPILOT.ASSISTANTS.SALES.DESCRIPTION'),
      icon: 'i-lucide-trending-up',
    },
    {
      id: 'events',
      name: t('COPILOT.ASSISTANTS.EVENTS.NAME'),
      description: t('COPILOT.ASSISTANTS.EVENTS.DESCRIPTION'),
      icon: 'i-lucide-calendar-days',
    },
    {
      id: 'booking_widgets',
      name: t('COPILOT.ASSISTANTS.BOOKING_WIDGETS.NAME'),
      description: t('COPILOT.ASSISTANTS.BOOKING_WIDGETS.DESCRIPTION'),
      icon: 'i-lucide-calendar-check-2',
    },
    {
      id: 'revops',
      name: t('COPILOT.ASSISTANTS.REVOPS.NAME'),
      description: t('COPILOT.ASSISTANTS.REVOPS.DESCRIPTION'),
      icon: 'i-lucide-receipt',
    },
    {
      id: 'insights',
      name: t('COPILOT.ASSISTANTS.INSIGHTS.NAME'),
      description: t('COPILOT.ASSISTANTS.INSIGHTS.DESCRIPTION'),
      icon: 'i-lucide-bar-chart-3',
    },
  ];
  if (betaEnabled.value) {
    list.unshift({
      id: 'rookoo-copilot-beta',
      name: t('COPILOT.ASSISTANTS.COPILOT_BETA.NAME'),
      description: t('COPILOT.ASSISTANTS.COPILOT_BETA.DESCRIPTION'),
      icon: 'i-lucide-bot',
    });
  }
  return list;
});

const {
  messages,
  toolCalls,
  subagents,
  isLoading,
  error,
  sendMessage,
  stopResponse,
  newThread,
  interrupt,
  hasPendingToolConsentInterrupt,
  respondToInterrupt,
  stream,
  fetchThreads,
  fetchToolConsents,
  addToolConsent,
  toolConsents,
  selectThread,
  threadId,
  isThreadSwitching,
  isThreadLoading,
  loadingPhase,
  ensureSession,
  getToken,
} = useCopilot({
  accountId: String(props.accountId),
  apiUrl: props.apiUrl,
  assistantType: props.assistantType,
  onOpenPanel: panel => emit('openPanel', panel),
});

const resolvedApiUrl = computed(
  () =>
    props.apiUrl ||
    window.globalConfig?.COPILOT_URL ||
    import.meta.env.VITE_COPILOT_URL ||
    ''
);

const isRenamingTitle = ref(false);
const titleDraft = ref('');
const titleInputRef = ref(null);
const titleRenamePending = ref(false);

const activeThread = computed(() =>
  store.threads.find(t => t.thread_id === threadId.value)
);

const headerTitle = computed(() => {
  if (activeThread.value) {
    return getThreadTitle(activeThread.value, t('COPILOT.EMPTY_THREAD'));
  }
  const firstHuman = messages.value?.find(m => m.type === 'human');
  if (firstHuman) {
    return deriveThreadTitle(firstHuman.content) || t('COPILOT.EMPTY_THREAD');
  }
  return t('COPILOT.EMPTY_THREAD');
});

function startTitleEdit() {
  if (!threadId.value || titleRenamePending.value) return;
  isRenamingTitle.value = true;
  titleDraft.value = headerTitle.value;
  nextTick(() => {
    titleInputRef.value?.focus();
    titleInputRef.value?.select();
  });
}

function cancelTitleEdit() {
  isRenamingTitle.value = false;
  titleDraft.value = '';
}

async function saveTitleEdit() {
  if (!isRenamingTitle.value) return;

  const nextTitle = titleDraft.value.trim();
  if (!nextTitle || !threadId.value) {
    cancelTitleEdit();
    return;
  }
  if (nextTitle === headerTitle.value) {
    cancelTitleEdit();
    return;
  }

  titleRenamePending.value = true;
  try {
    const ok = await store.renameThread(threadId.value, nextTitle, {
      apiUrl: resolvedApiUrl.value,
      getToken,
    });
    if (!ok) {
      useAlert(t('COPILOT.RENAME_FAILED'));
      return;
    }
    cancelTitleEdit();
  } finally {
    titleRenamePending.value = false;
  }
}

function handleTitleKeydown(event) {
  if (event.key === 'Enter') {
    event.preventDefault();
    saveTitleEdit();
  } else if (event.key === 'Escape') {
    event.preventDefault();
    cancelTitleEdit();
  }
}

const {
  pendingFiles,
  isUploading,
  addFiles,
  removeFile,
  clear: clearFiles,
  getUploadedFiles,
} = useCopilotAttachments({ accountId: props.accountId });

const vuexStore = useStore();
const homeInputRef = ref(null);
const chatInputRef = ref(null);

// Track the message the user just sent so it shows instantly before the
// optimistic/real update arrives from @langchain/vue.
const pendingUserContent = ref(null);

// Track when user explicitly starts a new thread — forces home view immediately
const isNewThread = ref(!threadId.value);

defineExpose({
  selectThread: id => {
    isNewThread.value = false;
    cancelTitleEdit();
    selectThread(id);
  },
  newThread: async () => {
    isNewThread.value = true;
    pendingUserContent.value = null;
    cancelTitleEdit();
    await newThread();
  },
});

watch(threadId, () => {
  cancelTitleEdit();
});

onMounted(() => {
  pickGreetingTitle();
  // Prefetch HttpOnly session cookie (and warm LangGraph caches via threads/search auth).
  ensureSession();
  fetchThreads();
  fetchToolConsents();
});

// Only enable streaming animation for responses triggered in this session.
// Prevents the SSE replay on page refresh from re-animating old messages.
const hasSentInSession = ref(false);

// Keep the skeleton up until the SDK finishes hydrating the full thread state.
// Clearing on first message caused a flash of a partial conversation.
// holdSkeletonForScroll also covers the post-hydrate window where LangGraph
// flushes getState via setTimeout(0) and SSE replay can briefly shrink the
// list to the first turn before catching up to the full history.
const holdSkeletonForScroll = ref(false);
let settleGeneration = 0;

const MESSAGE_SETTLE_MS = 300;
const MESSAGE_SETTLE_MAX_MS = 1500;

function waitMs(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

async function waitForMessagesToSettle(generation) {
  // Let the SDK's deferred getState flush land first.
  await waitMs(0);
  if (generation !== settleGeneration) return false;

  if (messages.value.length === 0) {
    // Empty threads don't get an SSE catch-up — reveal after the flush tick.
    return true;
  }

  const startedAt = Date.now();
  let lastLen = messages.value.length;
  let lastChangeAt = Date.now();
  // A length drop means SSE replay replaced the hydrated snapshot with an
  // earlier checkpoint — wait longer before treating that as "settled".
  let settleMs = MESSAGE_SETTLE_MS;

  while (Date.now() - startedAt < MESSAGE_SETTLE_MAX_MS) {
    await waitMs(50);
    if (generation !== settleGeneration) return false;
    if (isThreadSwitching.value || isThreadLoading.value) return false;

    const len = messages.value.length;
    if (len !== lastLen) {
      if (len < lastLen) settleMs = Math.max(settleMs, 450);
      lastLen = len;
      lastChangeAt = Date.now();
      continue;
    }
    if (Date.now() - lastChangeAt >= settleMs) return true;
  }

  return generation === settleGeneration;
}

function interruptPayload(raw) {
  return raw?.value ?? raw;
}

function isConsentLikeInterrupt(raw) {
  const value = interruptPayload(raw);
  if (value?.type === 'tool_consent') return true;
  if (Array.isArray(value?.action_requests) && value.action_requests.length) {
    return true;
  }
  return false;
}

const isLoadingThread = computed(() => {
  // Never keep the skeleton up while a tool-consent interrupt needs UI.
  if (isConsentLikeInterrupt(interrupt.value)) return false;

  return (
    isThreadSwitching.value ||
    isThreadLoading.value ||
    holdSkeletonForScroll.value
  );
});

const hasMessages = computed(
  () =>
    !isNewThread.value &&
    (messages.value.length > 0 || !!pendingUserContent.value)
);

const displayedMessages = computed(() => {
  let msgs = messages.value || [];
  if (pendingUserContent.value) {
    // If the SDK's optimistic update already includes our pending message as the last one, skip
    const last = msgs[msgs.length - 1];
    // eslint-disable-next-line no-underscore-dangle
    const lastIsHuman =
      last && (last.type === 'human' || last._getType?.() === 'human');
    if (lastIsHuman) {
      if (
        messageContentKey(last.content) !==
        messageContentKey(pendingUserContent.value)
      ) {
        msgs = [
          ...msgs,
          {
            id: PENDING_HUMAN_ID,
            type: 'human',
            content: pendingUserContent.value,
          },
        ];
      }
    } else {
      msgs = [
        ...msgs,
        {
          id: PENDING_HUMAN_ID,
          type: 'human',
          content: pendingUserContent.value,
        },
      ];
    }
  }

  // Reserve the AI message row (and feather slot) before the SDK adds the AI message.
  if (isLoading.value && msgs.length > 0) {
    const last = msgs[msgs.length - 1];
    const isLastHuman =
      // eslint-disable-next-line no-underscore-dangle
      last.type === 'human' || last._getType?.() === 'human';
    if (isLastHuman) {
      msgs = [
        ...msgs,
        {
          id: PENDING_AI_ID,
          type: 'ai',
          content: '',
          _copilotPending: true,
        },
      ];
    }
  }

  return msgs;
});

function getMessageReasoningMeta(index) {
  const msgs = displayedMessages.value;
  const msg = msgs[index];
  const isStreamingMessage =
    hasSentInSession.value &&
    isLoading.value &&
    index === msgs.length - 1 &&
    messageType(msg) === 'ai';

  return getTurnReasoningMeta(msgs, index, {
    isStreaming: isStreamingMessage,
  });
}

const messageReasoningMetas = computed(() =>
  displayedMessages.value.map((_, index) => getMessageReasoningMeta(index))
);

const expandedToolGroups = ref({});

function hasToolPayload(call) {
  if (!call) return false;
  const payload = call.output ?? call.result;
  if (payload == null) return false;
  if (typeof payload === 'string') return payload.length > 0;
  if (Array.isArray(payload)) return payload.length > 0;
  if (typeof payload === 'object') {
    if (Array.isArray(payload.content)) return payload.content.length > 0;
    if (typeof payload.content === 'string') return payload.content.length > 0;
    return Object.keys(payload).length > 0;
  }
  return true;
}

function displayToolStatus(call) {
  const status = call?.status;
  if (status === 'error') return 'error';
  if (['finished', 'complete'].includes(status)) return 'finished';
  if (!isLoading.value && !isToolConsentInterrupt.value) return 'finished';
  if (status === 'running' || status === 'pending') {
    if (call?.error) return 'error';
    if (hasToolPayload(call)) return 'finished';
  }
  return status || 'running';
}

const groupedToolCalls = computed(() => {
  const calls = (toolCalls.value || []).map(call => ({
    ...call,
    status: displayToolStatus(call),
  }));
  return calls.reduce((groups, call) => {
    const prev = groups[groups.length - 1];
    const sameAsPrev = prev && prev.status === call.status;

    if (sameAsPrev) {
      prev.calls.push(call);
      return groups;
    }

    groups.push({
      key: `${call.name}:${call.status}:${groups.length}`,
      name: call.name,
      status: call.status,
      calls: [call],
    });
    return groups;
  }, []);
});

const toolStatusById = computed(() =>
  (toolCalls.value || []).reduce((acc, call) => {
    const id = call?.callId || call?.id;
    if (id) acc[String(id)] = call.status;
    return acc;
  }, {})
);

function toggleToolGroup(groupKey) {
  expandedToolGroups.value = {
    ...expandedToolGroups.value,
    [groupKey]: !expandedToolGroups.value[groupKey],
  };
}

function isToolGroupExpanded(groupKey) {
  return !!expandedToolGroups.value[groupKey];
}

function getToolStatusMeta(status) {
  if (
    [
      'finished',
      'complete',
      'completed',
      'done',
      'success',
      'succeeded',
    ].includes(status)
  ) {
    return {
      label: 'Completed',
      icon: 'i-lucide-check-circle-2',
      colorClass: 'text-n-green-11',
      badgeClass: 'text-n-green-11 bg-n-green-3',
    };
  }

  if (['error', 'failed', 'failure', 'cancelled', 'aborted'].includes(status)) {
    return {
      label: 'Failed',
      icon: 'i-lucide-x-circle',
      colorClass: 'text-n-ruby-11',
      badgeClass: 'text-n-ruby-11 bg-n-ruby-3',
    };
  }

  if (['pending', 'queued'].includes(status)) {
    return {
      label: 'Pending',
      icon: 'i-lucide-clock-3',
      colorClass: 'text-n-amber-11',
      badgeClass: 'text-n-amber-11 bg-n-amber-3',
    };
  }

  return {
    label: 'Running',
    icon: 'i-lucide-loader-circle',
    colorClass: 'text-n-slate-10',
    badgeClass: 'text-n-slate-11 bg-n-solid-3',
  };
}

function getToolGroupSummary(group) {
  const stepCount = group?.calls?.length || 0;
  const stepWord = stepCount === 1 ? 'step' : 'steps';
  const status = group?.status;

  if (
    [
      'finished',
      'complete',
      'completed',
      'done',
      'success',
      'succeeded',
    ].includes(status)
  ) {
    return `Completed ${stepCount} ${stepWord}`;
  }

  if (['error', 'failed', 'failure', 'cancelled', 'aborted'].includes(status)) {
    return `Failed after ${stepCount} ${stepWord}`;
  }

  if (['pending', 'queued'].includes(status)) {
    return `Pending ${stepCount} ${stepWord}`;
  }

  return `Running ${stepCount} ${stepWord}`;
}

// --- Tool consent helpers ---

const isToolConsentInterrupt = computed(() =>
  isConsentLikeInterrupt(interrupt.value)
);

const pendingConsentTools = computed(() => {
  const val = interruptPayload(interrupt.value);
  if (Array.isArray(val?.tools) && val.tools.length) return val.tools;
  // Deep Agents HITL: action_requests[].name
  if (Array.isArray(val?.action_requests)) {
    return val.action_requests.map(r => r.name || r.tool || 'tool').filter(Boolean);
  }
  return [];
});

const uniqueConsentTools = computed(() => [
  ...new Set(pendingConsentTools.value),
]);

// Tracks tool consent outcome for in-message tool-log label animation.
const consentDecision = ref(null); // null | 'approved' | 'rejected'
let consentPhaseClearTimer = null;

const toolConsentConfirmationState = computed(() => {
  if (isToolConsentInterrupt.value) return 'approval-requested';
  return null;
});

// Only while waiting for Allow/Deny — after that the in-message tool rows update in place.
const showToolConsentConfirmation = computed(
  () => toolConsentConfirmationState.value === 'approval-requested'
);

const toolConsentPhase = computed(() => {
  if (isToolConsentInterrupt.value) return 'awaiting';
  if (consentDecision.value === 'approved') return 'approved';
  if (consentDecision.value === 'rejected') return 'rejected';
  return null;
});

const showTrailingToolCalls = computed(() => {
  if (!toolCalls.value?.length) return false;
  // Prefer in-message tool rows (keeps consent/approved next to them).
  if (showToolConsentConfirmation.value) return false;
  const msgs = displayedMessages.value;
  const last = msgs[msgs.length - 1];
  if (
    last &&
    (last.type === 'ai' || last._getType?.() === 'ai') &&
    last.tool_calls?.length
  ) {
    return false;
  }
  return true;
});

function scheduleConsentPhaseClear(decision, delayMs) {
  if (consentPhaseClearTimer) clearTimeout(consentPhaseClearTimer);
  consentPhaseClearTimer = setTimeout(() => {
    if (consentDecision.value === decision) consentDecision.value = null;
    consentPhaseClearTimer = null;
  }, delayMs);
}

function handleAllowOnce() {
  consentDecision.value = 'approved';
  scheduleConsentPhaseClear('approved', 700);
  respondToInterrupt({ approved: true });
}

async function handleAlwaysAllow() {
  for (const tool of pendingConsentTools.value) {
    await addToolConsent(tool);
  }
  consentDecision.value = 'approved';
  scheduleConsentPhaseClear('approved', 700);
  respondToInterrupt({ approved: true });
}

function handleDeny() {
  consentDecision.value = 'rejected';
  scheduleConsentPhaseClear('rejected', 1000);
  respondToInterrupt({ approved: false });
}

function resetToolConsentConfirmation() {
  if (consentPhaseClearTimer) {
    clearTimeout(consentPhaseClearTimer);
    consentPhaseClearTimer = null;
  }
  consentDecision.value = null;
}

watch(interrupt, val => {
  if (val && isToolConsentInterrupt.value) {
    // Never hide consent behind the home / new-thread empty state.
    isNewThread.value = false;
    resetToolConsentConfirmation();
  }
  if (isActiveTurn.value) {
    refreshTurnLayout('interrupt-changed');
  }
});

watch(consentDecision, () => {
  if (isActiveTurn.value) {
    refreshTurnLayout('consent-decision');
  }
});

// --- Suggestions ---

const suggestions = computed(() => {
  if (assistantType.value === 'rookoo-copilot-beta') {
    return [
      {
        icon: 'i-lucide-calendar-days',
        label: t('COPILOT.SUGGESTIONS.CHECK_EVENTS'),
        description: t('COPILOT.SUGGESTIONS.CHECK_EVENTS_DESC'),
        action: 'Check upcoming events and open one on the event sheet',
      },
      {
        icon: 'i-lucide-trending-up',
        label: t('COPILOT.SUGGESTIONS.PIPELINE_REVIEW'),
        description: t('COPILOT.SUGGESTIONS.PIPELINE_REVIEW_DESC'),
        action: 'Summarize the sales pipeline and flag stalled deals',
      },
      {
        icon: 'i-lucide-bar-chart-3',
        label: t('COPILOT.SUGGESTIONS.QUICK_SUMMARY'),
        description: t('COPILOT.SUGGESTIONS.QUICK_SUMMARY_DESC'),
        action: 'Analyze recent conversation trends and key insights',
      },
    ];
  }

  if (assistantType.value === 'seo') {
    return [
      {
        label: t('COPILOT.SUGGESTIONS.SEO_AUDIT'),
        description: t('COPILOT.SUGGESTIONS.SEO_AUDIT_DESC'),
        prompt:
          'Run an SEO audit on my knowledge base articles and suggest improvements.',
        icon: 'i-lucide-search-check',
      },
      {
        label: t('COPILOT.SUGGESTIONS.KEYWORD_RESEARCH'),
        description: t('COPILOT.SUGGESTIONS.KEYWORD_RESEARCH_DESC'),
        prompt:
          'Research trending keywords in my industry and suggest content opportunities.',
        icon: 'i-lucide-hash',
      },
      {
        label: t('COPILOT.SUGGESTIONS.OPTIMIZE_CONTENT'),
        description: t('COPILOT.SUGGESTIONS.OPTIMIZE_CONTENT_DESC'),
        prompt: 'Review my latest article and optimize it for search engines.',
        icon: 'i-lucide-file-text',
      },
    ];
  }
  if (assistantType.value === 'sales') {
    return [
      {
        label: t('COPILOT.SUGGESTIONS.PIPELINE_REVIEW'),
        description: t('COPILOT.SUGGESTIONS.PIPELINE_REVIEW_DESC'),
        prompt:
          'Show me my sales pipeline status — open deals, stalled opportunities, and next steps.',
        icon: 'i-lucide-trending-up',
      },
      {
        label: t('COPILOT.SUGGESTIONS.FOLLOW_UP'),
        description: t('COPILOT.SUGGESTIONS.FOLLOW_UP_DESC'),
        prompt: 'Which contacts need follow-up? Show me overdue interactions.',
        icon: 'i-lucide-reply',
      },
      {
        label: t('COPILOT.SUGGESTIONS.DRAFT_OUTREACH'),
        description: t('COPILOT.SUGGESTIONS.DRAFT_OUTREACH_DESC'),
        prompt: 'Help me draft a personalized outreach message for a prospect.',
        icon: 'i-lucide-mail',
      },
    ];
  }
  if (assistantType.value === 'events') {
    return [
      {
        label: t('COPILOT.SUGGESTIONS.CHECK_EVENTS'),
        description: t('COPILOT.SUGGESTIONS.CHECK_EVENTS_DESC'),
        prompt:
          'What upcoming events do I have? Show me the next confirmed ones.',
        icon: 'i-lucide-calendar-days',
      },
      {
        label: t('COPILOT.SUGGESTIONS.FIND_SLOT'),
        description: t('COPILOT.SUGGESTIONS.FIND_SLOT_DESC'),
        prompt: 'Find available time slots for a meeting next week.',
        icon: 'i-lucide-clock',
      },
      {
        label: t('COPILOT.SUGGESTIONS.RESOURCE_CHECK'),
        description: t('COPILOT.SUGGESTIONS.RESOURCE_CHECK_DESC'),
        prompt:
          'Check space availability and suggest optimal room allocation for my next event.',
        icon: 'i-lucide-map-pin',
      },
    ];
  }
  if (assistantType.value === 'revops') {
    return [
      {
        label: t('COPILOT.SUGGESTIONS.LIST_PLANS'),
        description: t('COPILOT.SUGGESTIONS.LIST_PLANS_DESC'),
        prompt: 'List all my pricing plans and show which one is the default.',
        icon: 'i-lucide-list',
      },
      {
        label: t('COPILOT.SUGGESTIONS.CREATE_SEASONAL_PLAN'),
        description: t('COPILOT.SUGGESTIONS.CREATE_SEASONAL_PLAN_DESC'),
        prompt:
          'Help me create a seasonal pricing plan for summer with adjusted rates.',
        icon: 'i-lucide-sun',
      },
      {
        label: t('COPILOT.SUGGESTIONS.SHOW_ITEMS_BY_CATEGORY'),
        description: t('COPILOT.SUGGESTIONS.SHOW_ITEMS_BY_CATEGORY_DESC'),
        prompt:
          'Show me all pricing items grouped by category (spaces, catering, AV, etc.).',
        icon: 'i-lucide-layers',
      },
    ];
  }
  return [
    {
      label: t('COPILOT.SUGGESTIONS.MANAGE_CONTACTS'),
      description: t('COPILOT.SUGGESTIONS.MANAGE_CONTACTS_DESC'),
      prompt: 'Show me my most recent contacts and their conversation status.',
      icon: 'i-lucide-contact',
    },
    {
      label: t('COPILOT.SUGGESTIONS.INBOX_OVERVIEW'),
      description: t('COPILOT.SUGGESTIONS.INBOX_OVERVIEW_DESC'),
      prompt:
        'Give me an overview of my inbox — how many open conversations, unattended, and any urgent ones.',
      icon: 'i-lucide-inbox',
    },
    {
      label: t('COPILOT.SUGGESTIONS.DRAFT_MESSAGE'),
      description: t('COPILOT.SUGGESTIONS.DRAFT_MESSAGE_DESC'),
      prompt:
        'Help me draft a professional reply for a customer who is asking about our pricing and availability.',
      icon: 'i-lucide-pen-line',
    },
    {
      label: t('COPILOT.SUGGESTIONS.QUICK_SUMMARY'),
      description: t('COPILOT.SUGGESTIONS.QUICK_SUMMARY_DESC'),
      prompt:
        'Summarize my team activity today — conversations handled, response times, and any escalations.',
      icon: 'i-lucide-bar-chart-3',
    },
  ];
});

// Track whether user is scrolled near the bottom (for non-turn scroll fallback)
const isNearBottom = ref(true);

function handleScroll() {
  if (!messageListRef.value) return;
  const { scrollTop, scrollHeight, clientHeight } = messageListRef.value;
  isNearBottom.value = scrollHeight - scrollTop - clientHeight < 80;
  handleTurnScroll();
}

function scrollToBottomIfNeeded(reason = 'scrollToBottomIfNeeded') {
  if (isLoadingThread.value) {
    return;
  }
  if (isLoading.value || isActiveTurn.value) {
    return;
  }
  if (!isNearBottom.value) {
    return;
  }
  pinScrollToBottom(reason);
}

// After a thread finishes loading, wait for the message list to stop growing
// (SSE replay catch-up), then pin to the latest message before first paint.
watch(
  () => isThreadSwitching.value || isThreadLoading.value,
  async (loading, wasLoading) => {
    if (loading) {
      settleGeneration += 1;
      holdSkeletonForScroll.value = true;
      isNearBottom.value = true;
      clearTurn();
      return;
    }
    if (!wasLoading && !holdSkeletonForScroll.value) return;

    const generation = settleGeneration;
    const settled = await waitForMessagesToSettle(generation);
    if (!settled || generation !== settleGeneration) return;

    // Drop the hold so the message list mounts, then pin scroll before paint.
    holdSkeletonForScroll.value = false;
    await nextTick();
    if (generation !== settleGeneration) return;
    pinScrollToBottom('thread-settled');
    requestAnimationFrame(() => {
      if (generation !== settleGeneration) return;
      pinScrollToBottom('thread-settled');
    });
  },
  { immediate: true }
);

watch(
  messages,
  newMsgs => {
    const prevPending = !!pendingUserContent.value;
    // Clear the local pending message once the SDK includes it (optimistic or real)
    if (pendingUserContent.value && newMsgs.length > 0) {
      const last = newMsgs[newMsgs.length - 1];
      // eslint-disable-next-line no-underscore-dangle
      if (last && (last.type === 'human' || last._getType?.() === 'human')) {
        pendingUserContent.value = null;
      }
    }
    if (prevPending && !pendingUserContent.value) {
      refreshTurnLayout('pending-user-cleared');
    }
  },
  { deep: true }
);

watch(
  () => displayedMessages.value.length,
  () => {
    if (isActiveTurn.value) {
      refreshTurnLayout('displayed-length-changed');
      return;
    }
    scrollToBottomIfNeeded('displayed-length-changed');
  }
);

watch(isLoading, (loading, wasLoading) => {
  if (loading && !wasLoading) {
    if (!isActiveTurn.value) {
      beginTurnAfterSend('is-loading-true');
    } else {
      refreshTurnLayout('is-loading-true');
    }
  }
  if (wasLoading && !loading) {
    nextTick(() => {
      finishTurn('is-loading-false');
      if (holdActiveAiKeyTimer) clearTimeout(holdActiveAiKeyTimer);
      holdActiveAiKeyTimer = setTimeout(() => {
        holdActiveAiKey.value = false;
        holdActiveAiKeyTimer = null;
      }, 400);
    });
  }
});

// Rotating thinking labels — picks one at random when a new response starts.
const THINKING_LABELS = [
  // Event/venue themed
  'Orchestrating...',
  'Setting the stage...',
  'Behind the scenes...',
  'Curating...',
  'Coordinating...',
  'Arranging...',
  'Staging...',
  'Pulling it together...',
  'Crafting...',
  'Preparing...',
  // Subtle/professional
  'Planning...',
  'Working on it...',
  'Connecting the dots...',
  'Figuring it out...',
  'Processing...',
];
const LOADING_PHASE_I18N = {
  authenticating: 'COPILOT.LOADING_AUTHENTICATING',
  connecting: 'COPILOT.LOADING_CONNECTING',
  starting: 'COPILOT.LOADING_STARTING',
  preparing: 'COPILOT.LOADING_PREPARING',
};
const thinkingLabel = ref(THINKING_LABELS[0]);
watch(isLoading, (loading, wasLoading) => {
  if (loading && !wasLoading && !loadingPhase.value) {
    thinkingLabel.value =
      THINKING_LABELS[Math.floor(Math.random() * THINKING_LABELS.length)];
  }
});

const displayThinkingLabel = computed(() => {
  const phaseKey = loadingPhase.value && LOADING_PHASE_I18N[loadingPhase.value];
  if (phaseKey) return t(phaseKey);
  return thinkingLabel.value;
});

function scrollToBottomClick() {
  pinScrollToBottom('scroll-to-bottom-click');
  isNearBottom.value = true;
}

// --- Follow-up suggestions ---

const followUpSuggestions = computed(() => {
  // Get suggestions from the last AI message's CopilotMessage component
  if (!lastMessageRef.value) return [];
  const suggs = lastMessageRef.value.suggestions;
  if (Array.isArray(suggs) && suggs.length > 0) return suggs.slice(0, 3);
  return [];
});

// --- Error display categorization ---
const errorDisplay = computed(() => {
  const err = error.value;
  if (!err) return {};
  const msg = err?.message || '';
  const isNetwork =
    msg.includes('fetch') ||
    msg.includes('network') ||
    msg.includes('Failed to fetch') ||
    msg.includes('ERR_CONNECTION');
  const isRateLimit = msg.includes('429') || msg.includes('rate limit');
  const isInterrupted = msg.includes('interrupted') || msg.includes('aborted');

  if (isNetwork) {
    return {
      title: t('COPILOT.ERROR_NETWORK'),
      detail: t('COPILOT.ERROR_NETWORK_DETAIL'),
      containerClass: 'bg-n-amber-2 border border-n-amber-6',
      iconClass: 'i-lucide-wifi-off text-n-amber-11',
      textClass: 'text-n-amber-11',
      detailClass: 'text-n-amber-10',
      buttonClass: 'border-n-amber-6 text-n-amber-11 hover:bg-n-amber-3',
    };
  }
  if (isRateLimit) {
    return {
      title: t('COPILOT.ERROR_RATE_LIMIT'),
      detail: t('COPILOT.ERROR_RATE_LIMIT_DETAIL'),
      containerClass: 'bg-n-amber-2 border border-n-amber-6',
      iconClass: 'i-lucide-clock text-n-amber-11',
      textClass: 'text-n-amber-11',
      detailClass: 'text-n-amber-10',
      buttonClass: 'border-n-amber-6 text-n-amber-11 hover:bg-n-amber-3',
    };
  }
  if (isInterrupted) {
    return {
      title: t('COPILOT.ERROR_INTERRUPTED'),
      detail: null,
      containerClass: 'bg-n-slate-3 border border-n-slate-6',
      iconClass: 'i-lucide-pause-circle text-n-slate-10',
      textClass: 'text-n-slate-11',
      detailClass: '',
      buttonClass: 'border-n-slate-6 text-n-slate-11 hover:bg-n-slate-4',
    };
  }
  return {
    title: t('COPILOT.ERROR_GENERIC'),
    detail: msg || null,
    containerClass: 'bg-n-ruby-3 border border-n-ruby-6',
    iconClass: 'i-lucide-alert-circle text-n-ruby-11',
    textClass: 'text-n-ruby-11',
    detailClass: 'text-n-ruby-10',
    buttonClass: 'border-n-ruby-6 text-n-ruby-11 hover:bg-n-ruby-4',
  };
});

async function handleSend(content, mentionedItems = []) {
  // Deny only via the consent card — keep composer quiet until user decides.
  if (isToolConsentInterrupt.value || hasPendingToolConsentInterrupt.value) {
    isNewThread.value = false;
    return;
  }

  isNewThread.value = false;
  resetToolConsentConfirmation();
  const displayContent = stripAttachedContext(content);
  pendingUserContent.value =
    mentionedItems.length > 0
      ? buildAttachedContextPreview(mentionedItems, displayContent)
      : displayContent;
  hasSentInSession.value = true;
  holdActiveAiKey.value = true;
  if (holdActiveAiKeyTimer) clearTimeout(holdActiveAiKeyTimer);

  let messageContent = content;
  const hasAttachedBlock =
    content.includes('<attached_context>') ||
    content.includes('<attached_events>');

  if (!hasAttachedBlock && mentionedItems.length > 0) {
    const fullRecordsByKey = {};
    await Promise.all(
      mentionedItems.map(async item => {
        const key = mentionItemKey(item);
        if (item.type === 'event') {
          fullRecordsByKey[key] = await vuexStore.dispatch(
            'planningEvents/show',
            item.id
          );
        } else {
          fullRecordsByKey[key] = item.raw || item;
        }
      })
    );
    const contextBlock = buildAttachedContext(mentionedItems, fullRecordsByKey);
    messageContent = `${contextBlock}\n\n${displayContent}`;
    pendingUserContent.value = messageContent;
  }

  const files = getUploadedFiles();
  sendMessage(messageContent, files);
  clearFiles();
  nextTick(() => beginTurnAfterSend('handle-send'));
}

async function handleNewThread() {
  isNewThread.value = true;
  resetToolConsentConfirmation();
  pendingUserContent.value = null;
  homeInputRef.value?.clearComposer();
  chatInputRef.value?.clearComposer();
  await newThread();
}

function handleRetry() {
  // Find the last human message and re-send it
  const msgs = messages.value || [];
  const lastHuman = [...msgs]
    .reverse()
    .find(m => m.type === 'human' || m._getType?.() === 'human');
  if (lastHuman) {
    const content =
      typeof lastHuman.content === 'string'
        ? lastHuman.content
        : lastHuman.content
            ?.filter(b => b.type === 'text')
            .map(b => b.text)
            .join('\n') || '';
    if (content) handleSend(content);
  }
}

// MCP connector popover
const mcpPopoverOpen = ref(false);
const mcpCopied = ref(false);

const mcpUrl = computed(
  () => `${window.location.origin}/api/v1/accounts/${props.accountId}/mcp`
);

function toggleMcpPopover() {
  mcpPopoverOpen.value = !mcpPopoverOpen.value;
  mcpCopied.value = false;
}

function closeMcpPopover() {
  mcpPopoverOpen.value = false;
}

async function copyMcpUrl() {
  await copyTextToClipboard(mcpUrl.value);
  mcpCopied.value = true;
  setTimeout(() => {
    mcpCopied.value = false;
  }, 2000);
}
</script>

<template>
  <div class="relative flex flex-col h-full min-h-0 bg-n-solid-1">
    <!-- Home state: no messages yet (only show for fresh/new threads) -->
    <div
      v-if="isNewThread || (!hasMessages && !isLoading && !threadId)"
      class="flex flex-col w-full h-full"
    >
      <!-- Header -->
      <header class="sticky top-0 z-10">
        <div class="max-w-7xl w-full mx-auto p-6 bg-n-surface-1">
          <div class="flex-1 flex flex-row justify-end">
            <div class="flex items-center gap-3">
              <div class="relative">
                <Button
                  id="mcpToggleHome"
                  outlined
                  slate
                  icon="i-lucide-usb"
                  :title="t('COPILOT.MCP.BUTTON')"
                  @click="toggleMcpPopover"
                />
                <div
                  v-if="mcpPopoverOpen"
                  v-on-click-outside="[
                    closeMcpPopover,
                    { ignore: ['#mcpToggleHome'] },
                  ]"
                  class="absolute top-full mt-2 right-0 w-80 p-4 rounded-xl border border-n-weak bg-n-solid-1 shadow-lg z-50"
                >
                  <h3 class="text-sm font-semibold text-n-slate-12 mb-1">
                    {{ t('COPILOT.MCP.TITLE') }}
                  </h3>
                  <p class="text-xs text-n-slate-10 mb-3">
                    {{ t('COPILOT.MCP.DESCRIPTION') }}
                  </p>
                  <div class="flex items-center gap-2">
                    <code
                      class="flex-1 px-3 py-2 text-xs rounded-lg bg-n-solid-3 text-n-slate-11 truncate select-all"
                    >
                      {{ mcpUrl }}
                    </code>
                    <button
                      class="flex items-center justify-center size-8 rounded-lg hover:bg-n-solid-3 transition-colors"
                      :title="t('COPILOT.MCP.COPY')"
                      @click="copyMcpUrl"
                    >
                      <span
                        class="size-4"
                        :class="
                          mcpCopied
                            ? 'i-lucide-check text-n-teal-11'
                            : 'i-lucide-copy text-n-slate-11'
                        "
                      />
                    </button>
                  </div>
                </div>
              </div>
              <Button
                outlined
                slate
                icon="i-lucide-panel-left-open"
                :title="t('COPILOT.SEARCH_CHATS')"
                @click="store.toggleSidebar()"
              />
            </div>
          </div>
        </div>
      </header>

      <div class="flex items-center justify-center w-full flex-1 -mt-12">
        <div class="flex flex-col items-center w-full max-w-4xl px-6">
          <!-- Greeting -->
          <div class="mb-8 text-center">
            <h1 class="text-3xl font-semibold text-n-slate-12">
              {{ greetingTitle }}
            </h1>
            <p class="mt-2 text-sm text-n-slate-10">
              {{ t('COPILOT.HOME_SUBTITLE') }}
            </p>
          </div>

          <!-- Input box -->
          <div class="w-full max-w-2xl">
            <CopilotInput
              ref="homeInputRef"
              v-model:assistant-type="assistantType"
              :is-loading="isLoading"
              :pending-files="pendingFiles"
              :is-uploading="isUploading"
              :assistants="assistants"
              variant="home"
              @send="handleSend"
              @stop="stopResponse"
              @add-files="addFiles"
              @remove-file="removeFile"
              @open-panel="emit('openPanel', $event)"
            />
          </div>

          <!-- Suggestion cards (2x2 grid) -->
          <div
            class="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-6 w-full max-w-2xl"
          >
            <button
              v-for="suggestion in suggestions"
              :key="suggestion.label"
              class="flex items-start gap-3 px-4 py-3 text-left rounded-xl border border-n-weak hover:bg-n-solid-3 hover:border-n-slate-7 transition-colors"
              @click="handleSend(suggestion.prompt)"
            >
              <span
                class="size-5 shrink-0 mt-0.5 text-n-slate-10"
                :class="suggestion.icon"
              />
              <div class="min-w-0">
                <span class="block text-sm font-medium text-n-slate-12">{{
                  suggestion.label
                }}</span>
                <span
                  v-if="suggestion.description"
                  class="block mt-0.5 text-xs text-n-slate-9 line-clamp-2"
                  >{{ suggestion.description }}</span
                >
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Chat state: has messages (skeleton overlays — never unmount chat for consent) -->
    <div v-else class="flex min-h-0 flex-1 flex-col relative overflow-hidden">
      <div
        v-show="isLoadingThread"
        class="absolute inset-0 z-20 flex flex-col bg-n-solid-1"
      >
        <div class="flex-1 overflow-hidden px-4 py-20">
          <div class="mx-auto max-w-[840px] space-y-6">
            <div class="flex justify-end">
              <div
                class="w-2/3 h-10 rounded-2xl bg-slate-200 dark:bg-slate-700 animate-pulse"
              />
            </div>
            <div class="flex justify-start">
              <div class="flex flex-col gap-2 w-3/4">
                <div
                  class="h-4 rounded bg-slate-200 dark:bg-slate-700 animate-pulse"
                />
                <div
                  class="h-4 rounded bg-slate-200 dark:bg-slate-700 animate-pulse w-5/6"
                />
                <div
                  class="h-4 rounded bg-slate-200 dark:bg-slate-700 animate-pulse w-2/3"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Header overlay -->
      <header class="absolute inset-x-0 top-0 z-10 pointer-events-none">
        <div
          class="pointer-events-auto max-w-7xl w-full mx-auto p-6 bg-gradient-to-b from-n-surface-1 from-60% to-transparent"
        >
          <div class="flex-1 flex flex-row justify-between">
            <div class="flex flex-1 items-center gap-4 min-w-0">
              <h1
                v-if="!store.sidebarOpen"
                class="text-xl font-bold text-woot-900 dark:text-white shrink-0"
              >
                {{ t('COPILOT.TITLE') }}
              </h1>
              <input
                v-if="isRenamingTitle"
                ref="titleInputRef"
                v-model="titleDraft"
                type="text"
                maxlength="80"
                class="reset-base min-w-0 max-w-md flex-1 !mb-0 px-2 py-1 text-sm font-medium rounded-md border border-n-brand bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                :aria-label="t('COPILOT.RENAME_CHAT')"
                @keydown="handleTitleKeydown"
                @blur="saveTitleEdit"
              />
              <button
                v-else
                type="button"
                class="min-w-0 max-w-md truncate px-2 py-1 -mx-2 text-sm font-medium text-n-slate-12 rounded-md hover:bg-n-solid-3 transition-colors text-left"
                :title="t('COPILOT.RENAME_CHAT')"
                :disabled="!threadId || titleRenamePending"
                @click="startTitleEdit"
              >
                {{ headerTitle }}
              </button>
            </div>
            <div class="flex items-center gap-3">
              <div class="relative">
                <Button
                  id="mcpToggleChat"
                  outlined
                  slate
                  icon="i-lucide-usb"
                  :title="t('COPILOT.MCP.BUTTON')"
                  @click="toggleMcpPopover"
                />
                <div
                  v-if="mcpPopoverOpen"
                  v-on-click-outside="[
                    closeMcpPopover,
                    { ignore: ['#mcpToggleChat'] },
                  ]"
                  class="absolute top-full mt-2 right-0 w-80 p-4 rounded-xl border border-n-weak bg-n-solid-1 shadow-lg z-50"
                >
                  <h3 class="text-sm font-semibold text-n-slate-12 mb-1">
                    {{ t('COPILOT.MCP.TITLE') }}
                  </h3>
                  <p class="text-xs text-n-slate-10 mb-3">
                    {{ t('COPILOT.MCP.DESCRIPTION') }}
                  </p>
                  <div class="flex items-center gap-2">
                    <code
                      class="flex-1 px-3 py-2 text-xs rounded-lg bg-n-solid-3 text-n-slate-11 truncate select-all"
                    >
                      {{ mcpUrl }}
                    </code>
                    <button
                      class="flex items-center justify-center size-8 rounded-lg hover:bg-n-solid-3 transition-colors"
                      :title="t('COPILOT.MCP.COPY')"
                      @click="copyMcpUrl"
                    >
                      <span
                        class="size-4"
                        :class="
                          mcpCopied
                            ? 'i-lucide-check text-n-teal-11'
                            : 'i-lucide-copy text-n-slate-11'
                        "
                      />
                    </button>
                  </div>
                </div>
              </div>
              <Button
                outlined
                slate
                icon="i-lucide-panel-left-open"
                :title="t('COPILOT.SEARCH_CHATS')"
                @click="store.toggleSidebar()"
              />
              <Button
                icon="i-lucide-plus"
                :label="t('COPILOT.NEW_THREAD')"
                @click="handleNewThread"
              />
            </div>
          </div>
        </div>
      </header>

      <!-- Transcript column -->
      <div class="flex min-h-0 flex-1 flex-col">
        <div
          ref="messageListRef"
          class="flex-1 overflow-y-auto overflow-x-hidden pt-24 [overflow-anchor:none]"
          data-copilot-scroll
          @scroll="handleScroll"
        >
          <div class="relative w-full min-h-full flex flex-col">
            <div
              class="mx-auto flex w-full max-w-[840px] flex-1 flex-col min-h-full pl-10 pr-4"
            >
              <div
                role="feed"
                aria-label="Chat messages"
                class="flex flex-1 flex-col"
              >
                <div
                  ref="transcriptSizerRef"
                  data-copilot-transcript-sizer
                  class="relative w-full"
                  :data-sizer-excess="sizerExcessPx"
                  :style="sizerExcessStyle"
                >
                  <CopilotMessage
                    v-for="(msg, index) in displayedMessages"
                    :key="transcriptRowKey(msg, index)"
                    :ref="el => setMessageRef(transcriptRowKey(msg, index), el)"
                    :message="msg"
                    :is-first="index === 0"
                    :tight-to-previous="isTightToPrevious(index)"
                    :is-last="index === displayedMessages.length - 1"
                    :is-streaming="
                      hasSentInSession &&
                      isLoading &&
                      index === displayedMessages.length - 1 &&
                      (msg.type === 'ai' || msg._getType?.() === 'ai')
                    "
                    :is-paused-for-tool-consent="
                      showToolConsentConfirmation &&
                      index === displayedMessages.length - 1 &&
                      (msg.type === 'ai' || msg._getType?.() === 'ai')
                    "
                    :tool-consent-phase="
                      index === displayedMessages.length - 1 &&
                      (msg.type === 'ai' || msg._getType?.() === 'ai')
                        ? toolConsentPhase
                        : null
                    "
                    :merged-turn-reasoning="
                      messageReasoningMetas[index]?.mergedTurnReasoning
                    "
                    :hide-reasoning="
                      messageReasoningMetas[index]?.hideReasoning
                    "
                    :reasoning-streaming="
                      messageReasoningMetas[index]?.reasoningStreaming
                    "
                    :suppress-reasoning-only-bubble="
                      messageReasoningMetas[index]?.suppressBubble
                    "
                    :tool-status-by-id="toolStatusById"
                    :pending-label="displayThinkingLabel"
                    @retry="handleRetry"
                    @open-panel="emit('openPanel', $event)"
                  >
                    <template
                      v-if="
                        showToolConsentConfirmation &&
                        index === displayedMessages.length - 1 &&
                        (msg.type === 'ai' || msg._getType?.() === 'ai')
                      "
                      #consent
                    >
                      <CopilotConfirmation
                        :state="toolConsentConfirmationState"
                      >
                        <template #title>
                          <div class="flex items-center gap-2 font-medium">
                            <span
                              class="i-lucide-shield-check size-4 text-n-slate-11"
                            />
                            {{ t('COPILOT.TOOL_CONSENT_PROMPT') }}
                          </div>
                        </template>

                        <template #request>
                          <div class="space-y-0.5">
                            <div
                              v-for="toolName in uniqueConsentTools"
                              :key="toolName"
                              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-solid-3"
                            >
                              <span
                                class="i-lucide-wrench size-3.5 text-n-slate-10"
                              />
                              <span class="text-sm text-n-slate-12">
                                {{ getToolLabel(toolName) }}
                              </span>
                            </div>
                          </div>
                        </template>

                        <template #actions>
                          <Button
                            size="sm"
                            :label="t('COPILOT.ALLOW_ONCE')"
                            @click="handleAllowOnce"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            color="slate"
                            :label="t('COPILOT.ALWAYS_ALLOW')"
                            @click="handleAlwaysAllow"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            color="slate"
                            :label="t('COPILOT.DENY')"
                            @click="handleDeny"
                          />
                        </template>
                      </CopilotConfirmation>
                    </template>
                  </CopilotMessage>

                  <div
                    data-copilot-transcript-trailing
                    class="relative w-full space-y-4"
                  >
                    <!-- Tool calls in progress (inline timeline) -->
                    <div v-if="showTrailingToolCalls" class="pl-0">
                      <div
                        v-for="group in groupedToolCalls"
                        :key="group.key"
                        class="space-y-1"
                      >
                        <CopilotToolCall
                          v-if="group.calls.length === 1"
                          :tool-call="group.calls[0]"
                        />

                        <div v-else>
                          <button
                            class="relative w-full flex items-center gap-2.5 py-2 px-0 text-left text-n-slate-11 rounded-md hover:bg-n-solid-2 hover:text-n-slate-12 transition-colors"
                            @click="toggleToolGroup(group.key)"
                          >
                            <span
                              class="size-4 absolute right-full mr-2 pointer-events-none"
                              :class="[
                                getToolStatusMeta(group.status).icon,
                                getToolStatusMeta(group.status).colorClass,
                                group.status === 'running'
                                  ? 'animate-spin'
                                  : '',
                              ]"
                            />
                            <span
                              class="min-w-0 flex-1 text-sm font-medium truncate"
                            >
                              {{ getToolGroupSummary(group) }}
                            </span>
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                              :class="
                                getToolStatusMeta(group.status).badgeClass
                              "
                            >
                              {{ getToolStatusMeta(group.status).label }}
                            </span>
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded bg-n-solid-3 text-xs font-medium text-n-slate-11"
                            >
                              {{ `x${group.calls.length}` }}
                            </span>
                            <span
                              class="i-lucide-chevron-right size-4 text-n-slate-9 shrink-0 transition-transform"
                              :class="{
                                'rotate-90': isToolGroupExpanded(group.key),
                              }"
                            />
                          </button>

                          <div
                            v-if="isToolGroupExpanded(group.key)"
                            class="space-y-0.5"
                          >
                            <div
                              v-for="tool in group.calls"
                              :key="tool.id"
                              class="relative flex items-center gap-2.5 py-1 text-n-slate-11"
                            >
                              <span
                                class="size-4 absolute right-full mr-2 pointer-events-none"
                                :class="[
                                  getToolStatusMeta(tool.status).icon,
                                  getToolStatusMeta(tool.status).colorClass,
                                  tool.status === 'running'
                                    ? 'animate-spin'
                                    : '',
                                ]"
                              />
                              <span
                                class="min-w-0 flex-1 truncate text-sm font-medium"
                                >{{
                                  getToolStatusLabel(tool.name, tool.status)
                                }}</span
                              >
                              <span
                                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                :class="
                                  getToolStatusMeta(tool.status).badgeClass
                                "
                              >
                                {{ getToolStatusMeta(tool.status).label }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Subagents -->
                    <template v-for="[id, subagent] in subagents" :key="id">
                      <CopilotSubagent :subagent="subagent" :stream="stream" />
                    </template>

                    <!-- Tool consent fallback when last row can't host #consent -->
                    <CopilotConfirmation
                      v-if="
                        showToolConsentConfirmation &&
                        !(
                          displayedMessages.length &&
                          (displayedMessages[displayedMessages.length - 1]
                            .type === 'ai' ||
                            displayedMessages[
                              displayedMessages.length - 1
                            ]._getType?.() === 'ai')
                        )
                      "
                      :state="toolConsentConfirmationState"
                    >
                      <template #title>
                        <div class="flex items-center gap-2 font-medium">
                          <span
                            class="i-lucide-shield-check size-4 text-n-slate-11"
                          />
                          {{ t('COPILOT.TOOL_CONSENT_PROMPT') }}
                        </div>
                      </template>

                      <template #request>
                        <div class="space-y-0.5">
                          <div
                            v-for="toolName in uniqueConsentTools"
                            :key="toolName"
                            class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-solid-3"
                          >
                            <span
                              class="i-lucide-wrench size-3.5 text-n-slate-10"
                            />
                            <span class="text-sm text-n-slate-12">
                              {{ getToolLabel(toolName) }}
                            </span>
                          </div>
                        </div>
                      </template>

                      <template #actions>
                        <Button
                          size="sm"
                          :label="t('COPILOT.ALLOW_ONCE')"
                          @click="handleAllowOnce"
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          color="slate"
                          :label="t('COPILOT.ALWAYS_ALLOW')"
                          @click="handleAlwaysAllow"
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          color="slate"
                          :label="t('COPILOT.DENY')"
                          @click="handleDeny"
                        />
                      </template>
                    </CopilotConfirmation>

                    <!-- Generic interrupt confirmation (non-tool-consent) -->
                    <CopilotConfirmation
                      v-if="interrupt && !isToolConsentInterrupt"
                      state="approval-requested"
                    >
                      <template #title>
                        <div class="flex items-center gap-2 font-medium">
                          <span
                            class="i-lucide-alert-triangle size-4 text-n-slate-11"
                          />
                          {{ t('COPILOT.INTERRUPT_PROMPT') }}
                        </div>
                      </template>

                      <template #request>
                        {{
                          interrupt?.value?.message ||
                          t('COPILOT.INTERRUPT_DEFAULT')
                        }}
                      </template>

                      <template #actions>
                        <Button
                          size="sm"
                          :label="t('COPILOT.APPROVE')"
                          @click="respondToInterrupt({ approved: true })"
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          color="slate"
                          :label="t('COPILOT.DENY')"
                          @click="respondToInterrupt({ approved: false })"
                        />
                      </template>
                    </CopilotConfirmation>

                    <!-- Error -->
                    <div
                      v-if="error"
                      class="flex items-start gap-3 p-3 rounded-lg"
                      :class="errorDisplay.containerClass"
                    >
                      <div
                        class="size-4 shrink-0 mt-0.5"
                        :class="errorDisplay.iconClass"
                      />
                      <div class="flex-1 min-w-0">
                        <p
                          class="text-sm font-medium"
                          :class="errorDisplay.textClass"
                        >
                          {{ errorDisplay.title }}
                        </p>
                        <p
                          v-if="errorDisplay.detail"
                          class="text-xs mt-0.5"
                          :class="errorDisplay.detailClass"
                        >
                          {{ errorDisplay.detail }}
                        </p>
                      </div>
                      <button
                        class="shrink-0 px-2.5 py-1 text-xs font-medium rounded-md border transition-colors"
                        :class="errorDisplay.buttonClass"
                        @click="handleRetry"
                      >
                        {{ t('COPILOT.TRY_AGAIN') }}
                      </button>
                    </div>

                    <div
                      v-if="
                        followUpSuggestions.length > 0 &&
                        !isLoading &&
                        !interrupt
                      "
                      data-copilot-followups
                      class="shrink-0 pt-2 pb-1"
                    >
                      <div class="flex flex-wrap justify-center gap-2">
                        <button
                          v-for="(suggestion, idx) in followUpSuggestions"
                          :key="idx"
                          class="inline-flex items-center h-7 px-2.5 text-xs rounded-lg border-0 outline-1 outline outline-transparent bg-n-slate-9/10 text-n-slate-12 hover:bg-n-slate-9/20 active:scale-[0.97] transition-all duration-100 ease-out"
                          @click="handleSend(suggestion)"
                        >
                          {{ suggestion }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  data-copilot-transcript-spacer
                  class="h-0 w-full shrink-0"
                  aria-hidden="true"
                />

                <div
                  class="h-px w-full shrink-0 pointer-events-none"
                  aria-hidden="true"
                  data-copilot-last-sentinel
                />
                <div class="h-12 shrink-0" aria-hidden="true" />
                <div
                  data-copilot-extra-space
                  class="grow min-h-0 shrink-0"
                  aria-hidden="true"
                />
              </div>
            </div>

            <!-- Sticky composer dock — full width -->
            <div
              data-chat-input-container
              class="sticky bottom-0 z-10 w-full bg-n-solid-1 backdrop-blur-md pb-6"
            >
              <div
                aria-hidden="true"
                data-copilot-transcript-bottom-fade
                class="pointer-events-none absolute inset-x-0 bottom-full h-16 bg-gradient-to-t from-n-solid-1 via-n-solid-1 to-transparent"
              />
              <div
                class="relative mx-auto w-full max-w-[840px] pl-10 pr-4 pt-6"
              >
                <button
                  v-if="!isNearBottom"
                  type="button"
                  class="z-[1] size-9 inline-flex items-center justify-center absolute -top-8 left-1/2 -translate-x-1/2 rounded-full border border-n-weak shadow-md bg-n-solid-1 hover:bg-n-solid-3 backdrop-blur transition-opacity"
                  :aria-label="t('COPILOT.SCROLL_TO_BOTTOM')"
                  @click="scrollToBottomClick"
                >
                  <span class="i-lucide-arrow-down size-5 text-n-slate-11" />
                </button>
                <CopilotInput
                  ref="chatInputRef"
                  v-model:assistant-type="assistantType"
                  :is-loading="isLoading"
                  :pending-files="pendingFiles"
                  :is-uploading="isUploading"
                  :assistants="assistants"
                  variant="chat"
                  @send="handleSend"
                  @stop="stopResponse"
                  @add-files="addFiles"
                  @remove-file="removeFile"
                  @open-panel="emit('openPanel', $event)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>
